import { Button, ConfigProvider, Form } from 'antd';
import React, { FC, ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { TbArrowsSort } from 'react-icons/tb';

import TransparentButton from '@/atoms/button-transparent';
import Dropdown from '@/atoms/dropdown';
import PropertyDynamicFilter from '@/molecules/property-dynamic-filter';
import { DropDownOption } from '@/types/common';
import { PredefinedStaticFilters, isFilterVisible } from '@/utils/isFilterVisible';
import {
  useGetAvailableAmenities,
  useGetAvailablePropertyTypes,
  useGetAvailableUnitTypeFilters,
  useGetAvailableUnitTypes,
  useGetDynamicFilterValues,
  useGetSelectedLocationType,
  useGetStaticFilters,
} from '@core/offices/actions/properties/filterActions';
import { sortDropDownOptions } from '@core/offices/actions/properties/useRentalForm';
import { StaticFilter, usePropertyFilters } from '@core/offices/store/filters';
import { COMPONENT_TYPE, FILTER_PREVIEW_TYPE } from '@core/offices/types';

import FilterPriceRange from '../property-dynamic-filter/filter-price-range';

interface Props {
  onSubmit: () => void;
  locationTypes: DropDownOption[];
  handleSubmit: (filters: StaticFilter) => void;
}

const OptionalDisplay = ({ type, children }: { type: PredefinedStaticFilters; children: ReactElement }) => {
  return isFilterVisible(type) ? children : null;
};

const PropertyAdvancedFilter: FC<Props> = ({ onSubmit, locationTypes, handleSubmit }) => {
  const { t } = useTranslation();
  const { updateFilterData, resetFilter, setAmenity, setLocationType, setUnitType, setMoveInDate } =
    usePropertyFilters();

  const selectedLocationType = useGetSelectedLocationType();
  const availableUnitTypes = useGetAvailableUnitTypes();
  const availablePropertyTypes = useGetAvailablePropertyTypes();
  const staticFilters = useGetStaticFilters();
  const dynamicFilters = useGetDynamicFilterValues();
  const unitTypeFilters = useGetAvailableUnitTypeFilters();
  const amenities = useGetAvailableAmenities();

  const applyChanges = useCallback(() => {
    onSubmit();
    handleSubmit(staticFilters);
  }, [onSubmit, handleSubmit, staticFilters]);

  return (
    <ConfigProvider
      theme={{
        token: { colorBgContainer: 'rgba(0, 0, 0, 0.03)' },
        components: { Form: { labelColor: '#707070', labelFontSize: 14 } },
      }}
    >
      <Form initialValues={dynamicFilters} layout="vertical" className="mb-16 relative overflow-y-auto pt-3">
        <OptionalDisplay type={PredefinedStaticFilters.LOCATION_TYPE}>
          <Form.Item initialValue={selectedLocationType} label={t('filters.locationType')}>
            <Dropdown
              allowClear={false}
              className="w-40 rounded-full h-9 text-xs"
              placeholder={t('filters.locationType')}
              options={locationTypes}
              name="locationType"
              value={selectedLocationType}
              onChange={(e) => setLocationType(e)}
            />
          </Form.Item>
        </OptionalDisplay>

        <OptionalDisplay type={PredefinedStaticFilters.UNIT_TYPE}>
          <Form.Item initialValue={staticFilters.unitType} label={t('filters.unitType')}>
            <Dropdown
              allowClear={false}
              className="w-40 rounded-full h-9 text-xs"
              placeholder={t('filters.unitType')}
              options={availableUnitTypes}
              name="unitType"
              value={staticFilters.unitType}
              onChange={(e) => {
                setUnitType(e);
              }}
            />
          </Form.Item>
        </OptionalDisplay>

        <OptionalDisplay type={PredefinedStaticFilters.PRICE_RANGE}>
          <Form.Item initialValue={staticFilters.priceRange} label={t('filters.priceRange')}>
            <FilterPriceRange handleSubmit={handleSubmit} />
          </Form.Item>
        </OptionalDisplay>

        <OptionalDisplay type={PredefinedStaticFilters.PROPERTY_TYPE}>
          <Form.Item initialValue={staticFilters.selectedPropertyTypes} label={t('filters.propertyType')}>
            <Dropdown
              className="w-40 rounded-full h-9 text-xs"
              placeholder={t('filters.propertyType')}
              options={availablePropertyTypes}
              name="propertyType"
              mode="multiple"
              maxTagCount="responsive"
              value={staticFilters.selectedPropertyTypes}
              onChange={(e) => handleSubmit({ ...staticFilters, selectedPropertyTypes: e })}
            />
          </Form.Item>
        </OptionalDisplay>

        <OptionalDisplay type={PredefinedStaticFilters.SORT}>
          <Form.Item initialValue={staticFilters.sort} label={t('filters.sort')}>
            <Dropdown
              allowClear={false}
              showSearch={false}
              suffixIcon={<TbArrowsSort size={16} />}
              key="sort"
              className="w-40 rounded-full h-9 text-xs"
              placeholder={t('filters.sortPlaceholder')}
              options={sortDropDownOptions}
              name="sort"
              value={staticFilters.sort}
              onChange={(e) => handleSubmit({ ...staticFilters, sort: e })}
            />
          </Form.Item>
        </OptionalDisplay>

        <PropertyDynamicFilter
          value={staticFilters.selectedAmenities}
          key="amenities"
          filter={{
            componentType: COMPONENT_TYPE.SELECT,
            fieldName: 'amenities',
            label: t('filters.amenities'),
            secondaryOperator: null,
            options: amenities,
            id: 8888800,
            maxLength: '',
            maxValue: null,
            minValue: null,
            isMultiSelect: '1',
            placeholder: t('filters.selectAmenities'),
            preview: FILTER_PREVIEW_TYPE.CARD,
          }}
          handleChange={(_name, value) => setAmenity(value)}
        />
        {/* move in date */}
        <PropertyDynamicFilter
          value={dynamicFilters.moveInDate?.value || undefined}
          key="moveInDate"
          filter={{
            componentType: COMPONENT_TYPE.DATE,
            fieldName: 'moveInDate',
            label: t('filters.availableDate'),
            secondaryOperator: null,
            options: [],
            id: 8888802,
            maxLength: null,
            maxValue: null,
            minValue: null,
            isMultiSelect: null,
            placeholder: t('filters.selectMoveInDate'),
            preview: null,
          }}
          handleChange={(_name, date) => setMoveInDate(date)}
        />
        {unitTypeFilters.map((availableFilter) => (
          <PropertyDynamicFilter
            value={dynamicFilters[availableFilter.fieldName]?.value || undefined}
            key={availableFilter.id}
            filter={availableFilter}
            handleChange={updateFilterData}
          />
        ))}
        <div className="flex gap-2 justify-between items-center w-full h-16">
          <div className="mb-2 cursor-pointer md:mb-0 w-1/2">
            <TransparentButton
              className="w-full !cursor-pointer h-11 text-start border-0 font-bold text-gray-500"
              onClick={resetFilter}
            >
              {t('filters.reset')}
            </TransparentButton>
          </div>
          <div className="mb-2 md:mb-0 w-1/2 me-0 md:me-2">
            <Button
              onClick={applyChanges}
              className="w-full h-11 hover:!text-black bg-secondary text-black font-bold"
              htmlType="submit"
            >
              {t('common.apply')}
            </Button>
          </div>
        </div>
      </Form>
    </ConfigProvider>
  );
};

export default PropertyAdvancedFilter;
